import { transparentize } from 'polished';
import styled, { css } from 'styled-components';

interface ITab {
  active: boolean;
  loading?: boolean;
}

interface IBox {
  $loading: boolean;
}

interface ITable {
  $loading: boolean;
}

interface IOpenButton {
  open: boolean;
  label: string;
}

interface IThTitleColumn {
  $order: boolean;
}

export const Loading = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    position: absolute;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${transparentize(0.7, theme.colors.black)};

    z-index: 11;
    cursor: progress;
  `}
`;

export const Error = styled.div`
  ${({ }) => css`
    width: 100%;
    height: 100%;
    position: absolute;
    pointer-events: none;

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 28px;
    font-weight: bold;
    padding-top: 55px;

    z-index: 10;
  `}
`;

export const OpenButton = styled.button<IOpenButton>`
  ${({ theme, open, label }) => css`
    pointer-events: auto;
    height: 20px;
    width: 160px;
    background-color: ${theme.colors.blue600};
    color: ${theme.colors.white};
    border-radius: 10px 10px 0 0;
    position: relative;

    @media (max-width: 810px) {
      width: 115px;
    }

    &:hover {
      &::before {
        opacity: ${open ? '0' : '1'};
      }
    }

    &::before {
      content: '${label}';
      padding: ${theme.spacings[6]};
      position: fixed;
      width: 150px;
      bottom: 25px;
      left: 50%;
      margin-left: -75px;

      font-family: ${theme.font.family.default};
      font-size: ${theme.font.sizes.xs};
      font-weight: ${theme.font.weights.bold};

      border: 1px solid ${theme.colors.white};
      border-radius: 0.4rem;

      background-color: ${theme.colors.black};
      color: ${theme.colors.white};

      opacity: 0;
      transition: all 0.2s;
      pointer-events: none;
    }
  `}
`;

export const Footer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.blue600};
    width: 100%;
    position: relative;
    height: 25px;
    width: 100%;
    padding: ${theme.spacings[6]} ${theme.spacings[8]};
    display: flex;
    align-items: right;
    color: #fff;
  `}
`;

export const Wrapper = styled.div<ITab & { zIndex: number }>`
  ${({ theme, active, zIndex }) => css`
    pointer-events: ${active ? 'auto' : 'none'};
    overflow: hidden;
    width: 100%;
    position: absolute;
    bottom: 0px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: ${zIndex};

    .react-resizable {
      width: 100%;
      bottom: 0px;
    }

    .react-resizable-handle {
      position: absolute;
      top: 20px;
      cursor: ns-resize;
      width: 100%;
      height: 14px;
      background-color: ${theme.colors.blue600};
    }
  `}
`;

export const Box = styled.div<IBox>`
  ${({ theme, $loading, }) => css`
    position: relative;
    height: 100%;
    width: 100%;
    bottom: 0px;
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.gray200};
    cursor: ${$loading ? 'progress' : 'auto'};

    .tab-navigation-container {
      background-color: ${theme.colors.blue600};
      display: flex;
      justify-content: space-between;
      padding-right: 5px;
    }
      
    .tab-wrapper {
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      padding: 9px 0px 0 0;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .buttons-wrapper {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      margin-left: 5px;
    }

    .clean {
      border: none;
      border: 2px solid ${theme.colors.blue600};
      color: ${theme.colors.blue600};
      font-family: ${theme.font.family.default};
      background-color: ${theme.colors.white};
      border-radius: 5px;
      transition: 0.5s;
      font-weight: ${theme.font.weights.semiBold};
      font-size: ${theme.font.sizes.sm};

      &:hover {
        background-color: ${theme.colors.blueGray900};
        color: ${theme.colors.white};
      }
      z-index: 1;
      display: flex;
      align-items: center;
      padding: 10px;
      
      @media (max-width: 1600px) {
        height: 30px;
      }

      @media (max-width: 535px) {
        height: 25px;
      }
    }

    .input-container {
      background-color: ${theme.colors.blueGray900};
      padding: 4px;
      display: flex;
    }

    .input-field {
      width: 100%;
      height: 37px;
      padding: 4px;
      border: none;
      outline: none;
      background-color: ${theme.colors.gray100};
      padding-right: 0px;
    }

    .search-input {
      font-size: 16px;
      font-weight: 500;
    }

    .simple-input {
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      gap: 5px;
      white-space: nowrap;
      &::-webkit-scrollbar {
        width: .5rem;
        height: .5rem;
      }

      &::-webkit-scrollbar-track-piece {
        background: ${theme.colors.gray50};
      }

      &::-webkit-scrollbar-track {
        background: ${theme.colors.blueGray900};
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${theme.colors.blue600};
      }
    }

    .button-input {
      width: 45px;
      background-color: ${theme.colors.gray100};
      font-size: 21px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .right-button {
      border-radius: 0 5px 5px 0;
      background-color: ${theme.colors.blue600};
    }

    .left-button {
      border-radius: 5px 0 0 5px;
      background-color: ${theme.colors.blue600};
    }

    .select-simple {
      appearance: none;
      text-align: left;
      text-align-last: center;
      width: auto;
    }

    .select-simple .MuiSelect-select {
      display: flex !important;
      justify-content: center !important;
      text-align: center !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      line-height: 1.5;
    }

    .text-simple {
      field-sizing: content;
    }

    .input-simple {
      flex-shrink: 0;
      min-width: 75px;
      height: 27px;
      font-size: 16px;
      font-weight: 500;

      padding: 4px 10px 4px 10px;

      border: 1px solid ${theme.colors.blueGray900};

      border-radius: 5px 5px 5px 5px;
    }
  `}
`;

export const TabButton = styled.button<ITab>`
  ${({ theme, active }) => css`
    background-color: ${active ? theme.colors.blueGray900 : theme.colors.blue600};
    border: 2px solid ${theme.colors.blueGray900};
    border-bottom: 0px;
    border-radius: ${theme.radius.sm} ${theme.radius.sm} 0 0;
    font-size: ${theme.font.sizes.md};
    color: ${theme.colors.white};
    padding: 3px 4px 3px 4px;
    margin: 5px 0 0 0;
    white-space: nowrap;

    :hover {
      background-color: ${active ? theme.colors.blueGray900 : theme.colors.blue700};
    }
  `}
`;

export const TableContainer = styled.div<ITable>`
  ${({ theme, $loading }) => css`
    position: relative;
    border-left: 1px solid black;
    width: 100%;
    height: 100%;
    padding-right: ${$loading ? '1rem' : ''};
    overflow: ${$loading ? 'hidden' : 'auto'};
    background: #b0c4de;

    &::-webkit-scrollbar {
      width: 1rem;
      height: 1rem;
    }

    &::-webkit-scrollbar-track-piece {
      background: ${theme.colors.gray50};
    }

    &::-webkit-scrollbar-track {
      background: ${theme.colors.blueGray900};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.blue700};
    }

    .search-header {
      padding: 4px;
    }

    table {
      border-collapse: collapse;
      width: 100%;
      font-size: ${theme.font.sizes.xxs};
      table-layout: fixed;

      .map-icon {
        font-size: 20px;
        cursor: pointer;
        margin-left: 5px;
      }

      th.small-cell, td.small-cell {
        width: 80px !important;
        max-width: 80px !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      thead {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
        z-index: 2;
      }

      .selected {
        background-color: ${theme.colors.blue300};
      }

      th {
        border: 1px solid ${theme.colors.blueGray900};
        padding: 4px;
        width: 14rem;
        height: 2rem;
      }

      td {
        border: 1px solid ${theme.colors.blueGray900};
        padding: 4px;
        width: 20rem;
        height: 45px;
      }

      th {
        background-color: ${theme.colors.blueGray900};
        color: white;
        border-top: 0;
        border-bottom: 0;
      }

      tr: nth-child(odd) {
        background-color: ${theme.colors.gray200};
      }

      tr: nth-child(even) {
        background-color: #b0c4de;
      }
    }
  `}
`;

export const TableThTitleColumn = styled.th<IThTitleColumn>`
  ${({ theme, $order }) => css`
    cursor: pointer;
    min-width: 160px;
    max-width: 220px;
    background-color: ${$order ? theme.colors.blueGray700 : theme.colors.blueGray900} !important;

    > div {
      width: 100%;
      max-width: 220px;
      display: flex;
      gap: 4px;
      align-items: center;
    }

    > div p {
      margin-bottom: 0;
      max-width: 180px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

export const ContentTitleColumn = styled.div`
  display: flex; 
  justify-content: center; 
  align-items: center;

  p {
    margin-bottom: 0;
    font-weight: normal;
  }
`
